import { ColumnDetails } from '../../../grid/types/common';
import { Column, ColumnKeys, INVESTIGATION_ACTIVITIES, POSSIBLE_ACTIONS, STATUS } from '../types';
import { html } from 'lit';

export function fileNameRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
  let cellValue = rowData.item[columnDetails.key];

  if (!cellValue || cellValue === 'undefined') {
    let fileState;
    const status = rowData.item['status'];
    switch (status) {
      case STATUS.PENDING:
      case STATUS.IN_PROGRESS:
        fileState = this.dependencies.translateFn('Processing...');
        break;
      case STATUS.FAILED:
        fileState = this.dependencies.translateFn('File not compiled');
        break;
      default:
        fileState = this.dependencies.translateFn('N/A');
        break;
    }
    cellValue = fileState;
  }

  return html`<span class="cell-label">${cellValue}</span>`;
}

export function statusRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
  const cellValue = rowData.item[columnDetails.key];
  let status, bubble;

  switch (cellValue) {
    case STATUS.IN_PROGRESS:
      status = this.dependencies.translateFn('In progress');
      bubble = 'bullet-neutral';
      break;
    case STATUS.FAILED:
      status = this.dependencies.translateFn('Failed');
      bubble = 'bullet-danger';
      break;
    case STATUS.FINISHED:
      status = this.dependencies.translateFn('Finished');
      bubble = 'bullet-success';
      break;
    case STATUS.TO_BE_RETRIEVED:
      status = this.dependencies.translateFn('File available');
      bubble = 'bullet-success';
      break;
    case STATUS.PENDING:
    default:
      status = this.dependencies.translateFn('Pending');
      bubble = 'bullet-neutral';
      break;
  }

  return html`<span class="cell-label has-bullet ${bubble}">${status}</span>`;
}

export function activityNameRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
  const cellValue = rowData.item[columnDetails.key];
  let activityName;

  switch (cellValue) {
    case INVESTIGATION_ACTIVITIES.COLLECT_INVESTIGATION_PACKAGE:
      activityName = this.dependencies.translateFn('Collect Investigation Package');
      break;
    case INVESTIGATION_ACTIVITIES.REMOTE_SHELL_DOWNLOAD:
      activityName = this.dependencies.translateFn('Remote Shell Download');
      break;
    default:
      activityName = '';
      break;
  }

  return html`<span class="cell-label">${activityName}</span>`;
}

export function startedOnRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
  const cellValue = rowData.item[columnDetails.key];

  if (!cellValue || cellValue === 'undefined') {
    return this.dependencies.translateFn('N/A');
  }

  return html`<span class="cell-label">${formatDate(cellValue)}</span>`;
}

export function finishedOnRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
  const cellValue = rowData.item[columnDetails.key];

  if (!cellValue || cellValue === 'undefined') {
    return this.dependencies.translateFn('Processing...');
  }

  return html`<span class="cell-label">${formatDate(cellValue)}</span>`;
}

export function fileExpirationDateRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
  const cellValue = rowData.item[columnDetails.key];

  if (!cellValue || cellValue === 'undefined') {
    let expirationDateState;
    const status = rowData.item['status'];
    switch (status) {
      case STATUS.PENDING:
      case STATUS.IN_PROGRESS:
        expirationDateState = this.dependencies.translateFn('Processing...');
        break;
      case STATUS.FAILED:
        expirationDateState = this.dependencies.translateFn('N/A');
        break;
      default:
        expirationDateState = this.dependencies.translateFn('N/A');
        break;
    }
    return expirationDateState;
  }

  return html`<span class="cell-label">${formatDate(cellValue)}</span>`;
}

export function actionsRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
  const action = rowData.item[columnDetails.key];
  let tooltipText = this.dependencies.userWithout2FA
    ? this.dependencies.translateFn('Enable 2FA on your account, to activate forensic data collection.')
    : '';

  let el;
  switch (action) {
    case POSSIBLE_ACTIONS.CANCEL:
      el = html`
        <gz-tooltip class="tooltip" tooltipText="${tooltipText}">
          <gz-button
            type="quiet"
            color="primary"
            lowercase="true"
            data-internal-id="${rowData.item.id}"
            data-item-type="${rowData.item.activity}"
            ?disabled="${this.dependencies.userWithout2FA}"
            @click="${this.onCancelItem.bind(this)}"
            buttonfontsize="small"
            buttonHeight="small"
            style="margin-left: -4px"
            >${this.dependencies.translateFn('Cancel')}
          </gz-button>
        </gz-tooltip>
      `;
      break;
    case POSSIBLE_ACTIONS.SEE_DETAILS:
      el = html`
        <gz-tooltip class="tooltip" tooltipText="${tooltipText}">
          <gz-button
            type="quiet"
            color="primary"
            lowercase="true"
            data-internal-id="${rowData.item.id}"
            ?disabled="${this.dependencies.userWithout2FA}"
            @click="${this.onSeeDetailsItem.bind(this)}"
            buttonfontsize="small"
            buttonHeight="small"
            style="margin-left: -4px"
            >${this.dependencies.translateFn('See details')}
          </gz-button>
        </gz-tooltip>
      `;
      break;
    case POSSIBLE_ACTIONS.DOWNLOAD_FILE:
      let disabled;
      if (rowData.item.activity === INVESTIGATION_ACTIVITIES.REMOTE_SHELL_DOWNLOAD) {
        disabled =
          !Application.Main.checkIfUserCanUseRemoteShellForCurrentCompany() ||
          !this.dependencies.companyIsEligibleForRemoteShell;

        if (!this.dependencies.companyIsEligibleForRemoteShell) {
          tooltipText = this.dependencies.translateFn(
            'Your company does not meet all the security criteria to download the file.'
          );
        } else {
          if (!Application.Main.checkIfUserCanUseRemoteShellForCurrentCompany()) {
            if (this.dependencies.userWithout2FA) {
              // 2FA error should be shown explicitly
              tooltipText = this.dependencies.translateFn('2FA must be enabled from My account.');
            } else {
              tooltipText = this.dependencies.translateFn(
                'Additional rights required. Contact your GravityZone administrator.'
              );
            }
          }
        }
      } else {
        disabled = this.dependencies.userWithout2FA;
      }

      el = html`
        <gz-tooltip class="tooltip" tooltipText="${tooltipText}">
          <gz-button
            type="quiet"
            color="primary"
            lowercase="true"
            data-internal-id="${rowData.item.id}"
            ?disabled="${disabled}"
            @click="${this.onDownloadFileItem.bind(this)}"
            buttonfontsize="small"
            buttonHeight="small"
            style="margin-left: -4px"
            >${this.dependencies.translateFn('Download file')}
          </gz-button>
        </gz-tooltip>
      `;
      break;
    case POSSIBLE_ACTIONS.RETRIEVE_FILE:
      el = html`
        <gz-tooltip class="tooltip" tooltipText="${tooltipText}">
          <gz-button
            type="quiet"
            color="primary"
            lowercase="true"
            data-internal-id="${rowData.item.id}"
            ?disabled="${this.dependencies.userWithout2FA}"
            @click="${this.onRetrieveFileItem.bind(this)}"
            buttonfontsize="small"
            buttonHeight="small"
            style="margin-left: -4px"
          >${this.dependencies.translateFn('Retrieve file')}
          </gz-button>
        </gz-tooltip>
      `;
      break;
    default:
      break;
  }

  return el;
}

export function formatDate(date) {
  const formatted = new Date(date),
    day = formatted.getDate(),
    month = formatted.toLocaleString('default', { month: 'short' }),
    year = formatted.getFullYear(),
    hour = ('0' + formatted.getHours()).slice(-2),
    minutes = ('0' + formatted.getMinutes()).slice(-2),
    seconds = ('0' + formatted.getSeconds()).slice(-2);

  return day + ' ' + month + ' ' + year + ', ' + hour + ':' + minutes + ':' + seconds;
}
